import { z } from 'zod';
import { ClassifiedEventSchema } from './classified-event.js';
import { DecimalStringSchema } from './primitives.js';

// What the automatic classifier produces for a transaction, recomputed live from
// the single classification engine. It always matches what would be applied, so
// the UI never shows a guess that diverges from the booked events.
export const AutoClassificationPreviewSchema = z.object({
  eventTypes: z.array(z.string()),
  isUnknown: z.boolean(),
});
export type AutoClassificationPreview = z.infer<typeof AutoClassificationPreviewSchema>;

export const TransferDirectionSchema = z.enum(['IN', 'OUT', 'NONE']);

export const EnrichedTransferSchema = z.object({
  logIndex: z.number().int(),
  assetId: z.number().int().positive(),
  assetSymbol: z.string(),
  assetDecimals: z.number().int().nonnegative(),
  assetIsNative: z.boolean(),
  fromAddr: z.string(),
  toAddr: z.string(),
  direction: TransferDirectionSchema,
  amountRaw: DecimalStringSchema,
  amountDecoded: DecimalStringSchema,
});
export type EnrichedTransfer = z.infer<typeof EnrichedTransferSchema>;

export const TxClassificationStatusSchema = z.enum(['UNKNOWN', 'CLASSIFIED', 'MANUAL']);
export type TxClassificationStatus = z.infer<typeof TxClassificationStatusSchema>;

export const TxMovementSchema = z.object({
  assetSymbol: z.string(),
  amount: DecimalStringSchema,
});
export type TxMovement = z.infer<typeof TxMovementSchema>;

export const TxMovementsSchema = z.object({
  in: z.array(TxMovementSchema),
  out: z.array(TxMovementSchema),
  gas: z.object({ amount: DecimalStringSchema, symbol: z.string() }).nullable(),
});
export type TxMovements = z.infer<typeof TxMovementsSchema>;

export const TxListRowSchema = z.object({
  rawTxId: z.number().int().positive(),
  txHash: z.string(),
  chainId: z.number().int(),
  chainSlug: z.string(),
  chainName: z.string(),
  blockNumber: z.number().int(),
  blockTimestamp: z.number().int(),
  status: z.number().int(),
  methodId: z.string().nullable(),
  counterpartyLabel: z.string().nullable(),
  walletId: z.number().int().positive(),
  walletLabel: z.string(),
  movements: TxMovementsSchema,
  movementSummary: z.string(),
  outCount: z.number().int().nonnegative(),
  inCount: z.number().int().nonnegative(),
  classification: z.object({
    status: TxClassificationStatusSchema,
    eventTypes: z.array(z.string()),
    eventCount: z.number().int().nonnegative(),
    hasOverride: z.boolean(),
    hasUnmatchedBridgeLeg: z.boolean().default(false),
  }),
});
export type TxListRow = z.infer<typeof TxListRowSchema>;

export const TxListResponseSchema = z.object({
  rows: z.array(TxListRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type TxListResponse = z.infer<typeof TxListResponseSchema>;

export const TxStatusCountsSchema = z.object({
  all: z.number().int().nonnegative(),
  classified: z.number().int().nonnegative(),
  unclassified: z.number().int().nonnegative(),
  ignored: z.number().int().nonnegative(),
  spam: z.number().int().nonnegative(),
});
export type TxStatusCounts = z.infer<typeof TxStatusCountsSchema>;

export const TransactionDetailsSchema = z.object({
  rawTxId: z.number().int().positive(),
  txHash: z.string(),
  chainId: z.number().int(),
  blockNumber: z.number().int(),
  blockTimestamp: z.number().int(),
  status: z.number().int(),
  methodId: z.string().nullable(),
  fromAddr: z.string(),
  toAddr: z.string().nullable(),
  valueWei: DecimalStringSchema,
  valueNative: DecimalStringSchema,
  gasUsed: z.number().int().nonnegative(),
  gasFeeNative: DecimalStringSchema,
  counterpartyLabel: z.string().nullable(),
  counterpartyCategory: z.string().nullable(),
  walletAddress: z.string(),
  walletId: z.number().int().positive(),
  nativeSymbol: z.string(),
  transfers: z.array(EnrichedTransferSchema),
  currentEvents: z.array(ClassifiedEventSchema),
  hasOverride: z.boolean(),
  autoClassification: AutoClassificationPreviewSchema,
});
export type TransactionDetails = z.infer<typeof TransactionDetailsSchema>;

export const TransactionSortSchema = z.object({
  sortBy: z.enum(['date', 'chain', 'type', 'status']).default('date'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export type TransactionSort = z.infer<typeof TransactionSortSchema>;
